import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, GitMerge, X, Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// Manual scan for duplicate TOURS (same guest, overlapping date). Lives in Settings —
// only runs when you tap it. Keep the most complete one and delete the extras.
export const TourDuplicateFinder = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(null);
  const [keep, setKeep] = useState({}); // groupId -> tourId to keep
  const [previewG, setPreviewG] = useState(null);

  const NUM_MAX = ["gratuity", "rating", "tip_percent", "total_hours"];
  const FL = { gratuity: "Gratuity", rating: "Rating", tip_percent: "Tip %", total_hours: "Total hours",
    resort_meet: "Resort / meet", meeting_time: "Meeting time", booking_status: "Status",
    party_size: "Party size", tour_type: "Type", co_guide: "Co-guide", notes: "Notes" };

  // Client-side preview mirroring the backend merge rules (fill blanks, concat notes, keep max).
  const computeMerged = (g) => {
    const keepId = keep[g.id] || g.keep_suggestion;
    const kept = g.tours.find((t) => t.id === keepId) || g.tours[0];
    const others = g.tours.filter((t) => t.id !== kept.id);
    const f = { ...(kept.fields || {}) };
    const dates = new Set(kept.dates || []);
    let notes = (f.notes || "").trim();
    others.forEach((o) => {
      (o.dates || []).forEach((d) => dates.add(d));
      Object.entries(o.fields || {}).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "") return;
        if (k === "notes") { const b = ("" + v).trim(); if (b && !notes.includes(b)) notes = notes ? notes + "\n\n" + b : b; }
        else if (NUM_MAX.includes(k)) { const cv = f[k]; if (cv == null || cv === "" || Number(v) > Number(cv)) f[k] = v; }
        else if (f[k] == null || f[k] === "") f[k] = v;
      });
    });
    f.notes = notes;
    return { keptTitle: kept.title, dates: [...dates].sort(), fields: f };
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.tourDuplicates();
      setGroups(d);
      const k = {};
      d.forEach((g) => { k[g.id] = g.keep_suggestion; });
      setKeep(k);
    } catch (e) { console.warn("tour-duplicates scan failed:", e); }
    finally { setLoading(false); }
  }, []);

  const mergeGroup = async (g) => {
    const keepId = keep[g.id] || g.keep_suggestion;
    const mergeIds = g.tours.filter((t) => t.id !== keepId).map((t) => t.id);
    if (mergeIds.length === 0) return;
    setWorking(g.id);
    try {
      await api.mergeTours(keepId, mergeIds);
      toast.success(`Merged ${mergeIds.length + 1} tours into one`);
      setGroups((prev) => prev.filter((x) => x.id !== g.id));
      onChanged?.();
    } catch {
      toast.error("Could not merge tours");
    } finally {
      setWorking(null);
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); refresh(); }}
        className="flex w-full items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
        data-testid="tour-duplicate-finder-btn"
      >
        <span className="flex items-center gap-2"><Copy className="h-4 w-4 text-amber-400" /> Check for duplicate tours</span>
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-zinc-300" /> : <span className="text-xs text-zinc-400">Scan now</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="grid-cols-[minmax(0,1fr)] max-h-[85vh] w-[calc(100vw-1rem)] !max-w-lg overflow-y-auto border-zinc-800 bg-zinc-950 p-4" data-testid="tour-duplicate-finder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
              <Copy className="h-5 w-5 text-amber-400" /> Possible duplicate tours
            </DialogTitle>
            <p className="text-sm text-zinc-400">Same guest booked on an overlapping date. Pick the one to keep — blank fields are filled from the others, notes are combined, and the highest tip is kept.</p>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /> Scanning…</div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-zinc-400" data-testid="tour-dup-empty">
              <ShieldCheck className="h-8 w-8 text-emerald-400" />
              No duplicate tours found.
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((g) => {
                const keepId = keep[g.id] || g.keep_suggestion;
                return (
                  <div key={g.id} className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3" data-testid={`tour-dup-group-${g.id}`}>
                    <p className="mb-2 text-xs font-600 uppercase tracking-wide text-zinc-300">{g.guest_name}</p>
                    <div className="space-y-1.5">
                      {g.tours.map((t) => {
                        const isKeep = t.id === keepId;
                        return (
                          <label key={t.id} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-2.5 py-2 ${isKeep ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-800 bg-zinc-950"}`}>
                            <input
                              type="radio"
                              name={`keep-${g.id}`}
                              checked={isKeep}
                              onChange={() => setKeep((p) => ({ ...p, [g.id]: t.id }))}
                              className="h-4 w-4 accent-emerald-500"
                              data-testid={`tour-dup-keep-${t.id}`}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-500 text-zinc-100">{t.title}</p>
                              <p className="text-[11px] text-zinc-400">
                                {(t.dates || []).join(", ")}
                                {t.meeting_time ? ` · ${t.meeting_time}` : ""}
                                {t.booking_status ? ` · ${t.booking_status}` : ""}
                              </p>
                            </div>
                            {isKeep && <span className="shrink-0 rounded-md bg-emerald-500/15 px-2 py-1 text-[10px] font-700 uppercase tracking-wide text-emerald-300">Keep</span>}
                          </label>
                        );
                      })}
                    </div>
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                      <button onClick={() => setGroups((prev) => prev.filter((x) => x.id !== g.id))} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-zinc-400 hover:text-zinc-300" data-testid={`tour-dup-skip-${g.id}`}>
                        <X className="h-3.5 w-3.5" /> Not duplicates
                      </button>
                      <button
                        onClick={() => setPreviewG(g)}
                        disabled={working === g.id}
                        className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-600 text-zinc-950 transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
                        data-testid={`tour-dup-merge-${g.id}`}
                      >
                        {working === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5" />}
                        Merge {g.tours.length} into one
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewG} onOpenChange={(o) => !o && setPreviewG(null)}>
        <DialogContent className="grid-cols-[minmax(0,1fr)] max-h-[85vh] w-[calc(100vw-1rem)] !max-w-md overflow-y-auto border-zinc-800 bg-zinc-950 p-4" data-testid="tour-merge-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
              <GitMerge className="h-5 w-5 text-amber-400" /> Preview merge
            </DialogTitle>
            <p className="text-sm text-zinc-400">{previewG?.tours.length} tours become one. Here's the combined result.</p>
          </DialogHeader>

          {previewG && (() => {
            const m = computeMerged(previewG);
            const rows = Object.entries(m.fields).filter(([, v]) => v !== null && v !== undefined && v !== "");
            return (
              <div className="space-y-3">
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="tour-merge-result">
                  <p className="text-xs font-600 uppercase tracking-wide text-emerald-300">After merge</p>
                  <p className="mt-1 text-sm font-500 text-zinc-100">{m.keptTitle}</p>
                  <p className="text-[11px] text-zinc-400">{m.dates.join(", ")}</p>
                  <div className="mt-2 space-y-1">
                    {rows.map(([k, v]) => (
                      <div key={k} className="flex gap-2 text-xs">
                        <span className="w-24 shrink-0 text-zinc-400">{FL[k] || k}</span>
                        <span className="min-w-0 flex-1 whitespace-pre-wrap text-zinc-200">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-zinc-400">Blank fields were filled from the other tours, notes combined, and the highest tip/rating kept.</p>
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={() => setPreviewG(null)} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="tour-merge-cancel">Cancel</Button>
                  <Button
                    onClick={async () => { const g = previewG; setPreviewG(null); await mergeGroup(g); }}
                    className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                    data-testid="tour-merge-confirm"
                  >
                    Confirm merge
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};
